/**
 * Quote totals: materials + labour + markup + overhead + optional tax.
 * @param {object} quote
 * @param {boolean} [quote.taxEnabled] — when false, no tax on materials; undefined preserves legacy (tax on).
 * @param {boolean} [quote.finalTaxEnabled] — when true, tax is applied to the full subtotal (materials+tax, labour, markup, overhead).
 */
export function calculateQuoteTotals(quote) {
  const materials = quote.materials || [];
  const labour = quote.labour || { entries: [], total: 0 };

  const materialsSubtotal = materials.reduce((sum, mat) => sum + (mat.qty * mat.price), 0);

  const labourTotal =
    labour.total ||
    labour.entries.reduce((sum, entry) => {
      const entryTotal = entry.total || (entry.hours * entry.rate) || 0;
      return sum + entryTotal;
    }, 0);

  const taxOn = quote.taxEnabled !== false;
  const materialsRateParsed = taxOn ? parseFloat(quote.taxRate) : 0;
  const materialsTaxRate = (Number.isFinite(materialsRateParsed) ? materialsRateParsed : 0) / 100;

  const materialsTax = materialsSubtotal * materialsTaxRate;
  const materialsWithTax = materialsSubtotal + materialsTax;

  const materialsAndLabourTotal = materialsWithTax + labourTotal;

  const markupAmount =
    quote.markupType === 'amount'
      ? parseFloat(quote.markup || 0) || 0
      : materialsAndLabourTotal * ((parseFloat(quote.markup || 0) || 0) / 100);
  const overheadAmount =
    quote.overheadType === 'amount'
      ? parseFloat(quote.overhead || 0) || 0
      : materialsAndLabourTotal * ((parseFloat(quote.overhead || 0) || 0) / 100);

  const subtotalAfterMarkup = materialsAndLabourTotal + markupAmount + overheadAmount;

  const finalTaxOn =
    quote.finalTaxEnabled === true ||
    quote.customQuoteData?.finalTaxEnabled === true;
  const finalRateParsed = parseFloat(quote.taxRate);
  const finalTaxRate =
    finalTaxOn && Number.isFinite(finalRateParsed) ? finalRateParsed / 100 : 0;
  const finalHstAmount = finalTaxOn ? subtotalAfterMarkup * finalTaxRate : 0;

  const total = subtotalAfterMarkup + finalHstAmount;

  return {
    materialsSubtotal,
    materialsTax,
    materialsWithTax,
    markupAmount,
    overheadAmount,
    labourTotal,
    materialsAndLabourTotal,
    subtotalAfterMarkup,
    finalHstAmount,
    total,
  };
}
